import React, { useState, useEffect, useMemo } from 'react';

const SCHOOL_COLORS = {
  Abjuration: '#b8860b', Conjuration: '#2e8b57', Divination: '#708090', Enchantment: '#c71585',
  Evocation: '#cc3300', Illusion: '#6a0dad', Necromancy: '#2f4f2f', Transmutation: '#1e90ff',
};

export default function SpellBook({ onClose, onCast }) {
  const [spells, setSpells] = useState([]);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch('/spells.json').then(r => r.json()).then(setSpells).catch(() => {});
  }, []);

  const classes = useMemo(() => {
    const set = new Set();
    spells.forEach(s => s.classes.forEach(c => set.add(c)));
    return Array.from(set).sort();
  }, [spells]);

  const filtered = useMemo(() => {
    return spells.filter(s => {
      if (search && !s.name.includes(search.toLowerCase()) && !s.desc.toLowerCase().includes(search.toLowerCase())) return false;
      if (levelFilter && s.level !== levelFilter) return false;
      if (schoolFilter && s.school !== schoolFilter) return false;
      if (classFilter && !s.classes.includes(classFilter)) return false;
      return true;
    });
  }, [spells, search, levelFilter, schoolFilter, classFilter]);

  return (
    <div className="sb-overlay" onClick={onClose}>
      <div className="sb-book" onClick={e => e.stopPropagation()}>
        <div className="sb-cover"></div>
        <div className="sb-spine"></div>
        <div className="sb-corner sb-corner-tl"></div>
        <div className="sb-corner sb-corner-bl"></div>
        <div className="sb-ribbon"></div>
        <button className="sb-close" onClick={onClose}>✕</button>

        <div className="sb-stitches">
          {Array(15).fill(0).map((_, i) => <div key={i} className="sb-stitch" />)}
        </div>

        <div className="sb-pages">
          {/* Left Page: Index */}
          <div className="sb-page sb-page-left">
            <h2 className="sb-heading">⚜ Arcane Index ⚜</h2>
            <input type="text" className="sb-search" placeholder="Search the tome..." value={search} onChange={e => setSearch(e.target.value)} />
            <div className="sb-filters">
              <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
                <option value="">All Levels</option>
                {['Cantrip','1st','2nd','3rd','4th','5th','6th','7th','8th','9th'].map(l => <option key={l}>{l}</option>)}
              </select>
              <select value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)}>
                <option value="">All Schools</option>
                {Object.keys(SCHOOL_COLORS).map(s => <option key={s}>{s}</option>)}
              </select>
              <select value={classFilter} onChange={e => setClassFilter(e.target.value)}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="sb-spell-list">
              {filtered.slice(0, 50).map(spell => (
                <div key={spell.name} className={`sb-entry ${selected?.name === spell.name ? 'selected' : ''}`} onClick={() => setSelected(spell)}>
                  <div className="sb-entry-name" style={{ color: SCHOOL_COLORS[spell.school] || '#3d1f0a' }}>{spell.name}</div>
                  <div className="sb-entry-meta">{spell.level} {spell.school} · {spell.range}</div>
                </div>
              ))}
              {filtered.length > 50 && <div className="sb-more">{filtered.length} spells — refine search</div>}
            </div>
            <span className="sb-page-num sb-num-left">— i —</span>
          </div>

          {/* Right Page: Detail */}
          <div className="sb-page sb-page-right">
            {selected ? (
              <div className="sb-detail">
                <div className="sb-flourish">❧ ❦ ❧</div>
                <div className="sb-detail-title">{selected.name}</div>
                <div className="sb-detail-subtitle">{selected.level} {selected.school}</div>
                <div className="sb-stat-row"><span className="sb-stat-label">Range</span><span>{selected.range}</span></div>
                <div className="sb-stat-row"><span className="sb-stat-label">School</span><span style={{ color: SCHOOL_COLORS[selected.school] }}>{selected.school}</span></div>
                <div className="sb-stat-row"><span className="sb-stat-label">Classes</span><span>{selected.classes.join(', ')}</span></div>
                <div className="sb-description">{selected.desc}</div>
                <button className="sb-cast-btn" onClick={() => { onCast(selected.name); onClose(); }}>⚡ Invoke This Spell</button>
                <div className="sb-flourish">✦ ✦ ✦</div>
              </div>
            ) : (
              <div className="sb-detail">
                <div className="sb-flourish">❧ ❦ ❧</div>
                <p className="sb-placeholder">Select a spell from the index<br/>to reveal its secrets...</p>
                <div className="sb-flourish">✦ ✦ ✦</div>
              </div>
            )}
            <span className="sb-page-num sb-num-right">— ii —</span>
          </div>
        </div>
      </div>
    </div>
  );
}
